import Analytics, {
  InteractionInputEvent,
  MilestoneInputEvent,
  OptimisationInputEvent,
} from '@ecs-uk/web-common-analytics';

function interactionEvent(component, action, status, data) {
  return InteractionInputEvent.fromObject({
    component,
    action,
    status,
    ...(data && { data }),
  });
}

// ECD2 events for Payment and billing page
export const publishChangeBillDateClick = () => {
  Analytics.publish(interactionEvent('change_bill_date_button', 'click', 'success'));
};

export const publishChangeBillDateEligibilityData = eligibilityData => {
  Analytics.publish(
    MilestoneInputEvent.fromObject({
      change_bill_date_eligibility: {
        is_eligible_to_change_bill_date: eligibilityData.isEligible ?? 'not_set',
        ineligible_reason: eligibilityData.ineligibilityReason ?? 'none',
      },
    })
  );
};

// ECD2 events for Choose new bill date page
export const publishClickedOnContinueForChangeBillingDate = newSelectedDate => {
  Analytics.publish(
    interactionEvent('choose_new_bill_date_continue_button', 'click', 'success', {
      selected_new_bill_date: newSelectedDate ?? 'not_set',
    })
  );
};

export const publishCancelClick = pageName => {
  Analytics.publish(interactionEvent(`${pageName}_cancel_button`, 'click', 'success'));
};

// ECD2 events for Review and pay page
export const publishEditBillDateClick = () => {
  Analytics.publish(interactionEvent('review_and_pay_edit_button', 'click', 'success'));
};

export const publishTryAgainClick = () => {
  Analytics.publish(interactionEvent('change_bill_date_try_again_button', 'click', 'success'));
};

export const publishChangeBillDate3dsCancel = () => {
  Analytics.publish(interactionEvent('change_bill_date_3ds_cancel', 'click', 'success'));
};

export const trackBillDateChangeCvvValid = () => {
  Analytics.publish(
    InteractionInputEvent.fromObject({
      component: 'change_bill_date_cvv',
      action: 'input',
      status: true,
    })
  );
};

export const publishReviewAndPayBillDateChangeData = billDateData => {
  Analytics.publish(
    MilestoneInputEvent.fromObject({
      one_time_payment_bill_date_change: {
        current_bill_date: billDateData.currentBillDate ?? 'not_set',
        selected_bill_date: billDateData.selectedBillDate ?? 'not_set',
        adjustment_days: billDateData.adjustmentDays ?? 'not_set',
        one_time_payment_amount_due: billDateData.oneTimePaymentAmountDue ?? 'not_set',
      },
    })
  );
};

export const publishOneTimePaymentCalculationFailure = selectedBillDate => {
  Analytics.publish(
    MilestoneInputEvent.fromObject({
      one_time_payment_calculation_failure: {
        selected_bill_date: selectedBillDate ?? 'not_set',
      },
    })
  );
};

export const publishConfirmPurchaseClick = (successful, paymentMethodType) => {
  Analytics.publish(
    InteractionInputEvent.fromObject({
      component: 'change_bill_date_confirm_purchase_button',
      action: 'click',
      status: successful,
      data: {
        payment_method: paymentMethodType,
      },
    })
  );
};

// Normalise payment method identifiers to match Snowplow schema expectations (snake_case)
const PAYMENT_METHOD_NORMALISATION = {
  applePay: 'apple_pay',
};

// Data 4.0 event to track the result of attempting to tokenize the payment method
export const trackPaymentMethodTokenizationResult = (successful, paymentMethod) => {
  Analytics.trackSelfDescribingEvent({
    schema: 'iglu:uk.co.experian.subscriptions/payment_method_tokenization_attempted/jsonschema/1-0-0',
    data: {
      successful,
      payment_method: PAYMENT_METHOD_NORMALISATION[paymentMethod] ?? paymentMethod,
      journey: 'change_billing_date',
    },
  });
};

// Data 4.0 event for when the output token is received from 3DS challenge
export const track3dsOutputTokenReceived = () => {
  Analytics.trackSelfDescribingEvent({
    schema: 'iglu:uk.co.experian.subscriptions/three_d_secure_output_token_received/jsonschema/1-0-0',
    data: {
      journey: 'change_billing_date',
    },
  });
};

// Data 4.0 event for when there is a failure in the 3DS challenge
export const track3dsChallengeFailure = (errorCode, errorMessage) => {
  Analytics.trackSelfDescribingEvent({
    schema: 'iglu:uk.co.experian.subscriptions/three_d_secure_failed/jsonschema/1-0-0',
    data: {
      journey: 'change_billing_date',
      error_code: errorCode ?? 'unknown',
      error_message: errorMessage ?? 'unknown',
    },
  });
};

export const trackChangeBillDateInitiatePurchase = successful => {
  Analytics.publish(
    MilestoneInputEvent.fromObject({
      change_bill_date_initiate_purchase: {
        interaction_status: successful,
      },
    })
  );
};

export const publishChallengePageRendered = () => {
  Analytics.publish(
    MilestoneInputEvent.fromObject({
      change_bill_date_challenge_page_load: {
        interaction_status: true,
      },
    })
  );
};

export const publishChallengePageRenderError = errorCode => {
  Analytics.publish(
    MilestoneInputEvent.fromObject({
      change_bill_date_3DS_auth_error: {
        error_type: errorCode ?? 'unknown',
      },
    })
  );
};

// Users interacted with 3DS challenge (either correct or wrong code or cancel)
export const publishChangeBillingDate3dsAuthSuccess = () => {
  Analytics.publish(
    MilestoneInputEvent.fromObject({
      change_bill_date_3DS_auth_success: {
        interaction_status: true,
      },
    })
  );
};

// After getting the output token from 3DS and attempting to authorize the purchase, track whether it was successful or not
export const trackChangeBillDateAuthorizePurchase = successful => {
  Analytics.publish(
    MilestoneInputEvent.fromObject({
      change_bill_date_authorize_purchase: {
        interaction_status: successful,
      },
    })
  );
};

export const publishOneTimePaymentSuccess = paymentData => {
  Analytics.publish(
    MilestoneInputEvent.fromObject({
      bill_date_change_payment_success: {
        old_bill_date: paymentData.oldBillDate ?? 'not_set',
        new_bill_date: paymentData.newBillDate ?? 'not_set',
        adjustment_days: paymentData.adjustmentDays ?? 'not_set',
        one_time_amount_paid: paymentData.amountPaid ?? 'not_set',
        one_time_amount_payment_method: paymentData.paymentMethod ?? 'not_set',
      },
    })
  );
};

export const publishBillDateChangeFailure = failureData => {
  Analytics.publish(
    MilestoneInputEvent.fromObject({
      bill_date_change_payment_failure: {
        attempted_payment_amount: failureData.attemptedPaymentAmount ?? 'not_set',
      },
    })
  );
};

export const trackChangeBillingDateAvailability = treatment => {
  Analytics.publishOnce(
    OptimisationInputEvent.fromObject({
      testName: 'change_bill_date',
      testArea: 'Product Movement',
      testVariant: treatment,
    })
  );
};
